// ============================================================
// SERVICE PACKAGES — Velocity Cleaning & Maintenance
// ============================================================

export const SERVICE_CATEGORIES = [
  {
    key: "exterior",
    label: "Exterior Care",
    headline: "Exterior Care",
    description: "Restore the shine. Protect the finish.",
    packages: [
      {
        slug: "exterior-maintenance",
        name: "Exterior Maintenance",
        price: 100,
        tagline: "Maintain the shine. Protect the finish.",
        description:
          "Designed for regularly maintained vehicles. Safely cleans, restores and protects your exterior while preserving its premium appearance.",
        image: "/photos/car1.jpg",
        features: [
          "Luxury snow foam pre-wash",
          "Safe hand wash",
          "Wheels cleaned",
          "Tyres cleaned & dressed",
          "Door jambs cleaned",
          "Exterior windows cleaned",
          "Protective spray sealant",
          "Final quality inspection",
        ],
      },
      {
        slug: "premium-exterior",
        name: "Premium Exterior",
        price: 180,
        tagline: "Beyond clean. Engineered to impress.",
        featured: true,
        description:
          "For owners who expect more than a standard wash. Removes embedded contamination, restores gloss and delivers a deeper, richer finish.",
        image: "/photos/paint.jpg",
        features: [
          "Everything in Exterior Maintenance",
          "Paint decontamination",
          "Clay bar treatment (where required)",
          "Premium paint protection",
          "Exterior trim restoration",
          "Detailed wheel finish",
          "Premium finishing touches",
        ],
      },
    ],
  },
  {
    key: "interior",
    label: "Interior Care",
    headline: "Interior Care",
    description: "Cabin reborn. Surfaces protected.",
    packages: [
      {
        slug: "interior-maintenance",
        name: "Interior Maintenance",
        price: 150,
        tagline: "Fresh. Refined. Ready for every journey.",
        description:
          "A complete interior refresh for vehicles that are already well maintained. Restores that fresh feeling and protects the surfaces you use every day.",
        image: "/photos/interior-maintenance.jpg",
        features: [
          "Thorough interior vacuum",
          "Dashboard & console cleaned",
          "Door trims cleaned",
          "Interior glass cleaned",
          "Cup holders detailed",
          "Boot vacuum",
          "Light dust removal",
          "Interior finishing touch",
        ],
      },
      {
        slug: "deep-interior",
        name: "Deep Interior Restoration",
        price: 250,
        tagline: "Restore the cabin. Rediscover the drive.",
        featured: true,
        description:
          "For heavily used or neglected interiors. We meticulously remove built-up dirt, stains and grime to restore your cabin to a premium standard.",
        image: "/photos/interior-deep.jpg",
        features: [
          "Everything in Interior Maintenance",
          "Deep carpet cleaning",
          "Seat shampoo / extraction",
          "Stain treatment",
          "Deep plastic cleaning",
          "Detailed crevice cleaning",
          "Leather conditioning (where applicable)",
          "Boot restoration",
          "Odour neutralisation",
        ],
      },
    ],
  },
];

export const BUNDLE_PACKAGES = [
  {
    slug: "maintenance-detail",
    name: "Maintenance Detail",
    price: 190,
    badge: "Most Popular",
    badgeIcon: "star",
    tagline: "Preserve the finish. Drive with pride.",
    description:
      "Designed for regularly maintained vehicles, our Maintenance Detail combines professional interior and exterior care to preserve your vehicle's appearance, protect its finish, and keep it looking its absolute best all year round.",
    image: "/photos/complete.jpg",
    includes: ["Exterior Maintenance", "Interior Maintenance"],
  },
  {
    slug: "premium-reset",
    name: "Premium Reset Detail",
    price: 320,
    badge: "Signature Service",
    badgeIcon: "crown",
    featured: true,
    tagline: "Restored beyond expectations.",
    description:
      "Our most comprehensive detailing experience. Designed to restore neglected vehicles inside and out through meticulous craftsmanship, premium products, and uncompromising attention to detail.",
    image: "/photos/car3.jpg",
    includes: ["Premium Exterior", "Deep Interior Restoration"],
  },
];

// ============================================================
// FLAT LIST for the quote form dropdown
// ============================================================
export const ALL_PACKAGES = [
  ...SERVICE_CATEGORIES.flatMap((cat) =>
    cat.packages.map((p) => ({ ...p, category: cat.label, categoryKey: cat.key }))
  ),
  ...BUNDLE_PACKAGES.map((p) => ({ ...p, category: "Bundle & Save", categoryKey: "bundle" })),
];

// ============================================================
// ADD-ONS
// ============================================================
export const ADD_ONS = [
  { name: "Pet Hair Removal", price: 30 },
  { name: "Engine Bay Detail", price: 50 },
];

// ============================================================
// PRICING NOTE & TRAVEL
// ============================================================
export const PRICING_NOTE =
  "Final pricing may vary depending on vehicle size, condition, pet hair, excessive dirt, and travel distance.";

export const TRAVEL_FEES = [
  { range: "0–15 km", fee: "Free", highlight: true },
  { range: "15–30 km", fee: "$20" },
  { range: "30–50 km", fee: "$40" },
  { range: "50 km+", fee: "Quote Required" },
];

// ============================================================
// SERVICE AREAS · TESTIMONIALS · GALLERY · FAQ
// ============================================================
export const SERVICE_AREAS = [
  "Perth CBD", "Piara Waters", "Joondalup", "Fremantle", "Rockingham",
  "Mandurah", "Armadale", "Cockburn", "Cannington", "Stirling",
  "Wanneroo", "Kalamunda", "Swan Valley", "Subiaco", "Scarborough",
  "Victoria Park", "Belmont", "Canning Vale",
];

export const TESTIMONIALS = [
  {
    name: "Marcus Hale",
    role: "Tesla Model S owner",
    rating: 5,
    text: "These guys came to my driveway and made my black Model S look better than the day I picked it up. Paint correction alone was worth every dollar.",
  },
  {
    name: "Priya Shah",
    role: "Range Rover Velar",
    rating: 5,
    text: "Two kids and a dog — interior was a disaster. They steam cleaned every inch and the cabin smells incredible. Booking the monthly maintenance now.",
  },
  {
    name: "Jordan Reyes",
    role: "BMW M3 Competition",
    rating: 5,
    text: "Professional, on-time, and they actually care about the finish. Decon, clay, polish, wax — paint feels like glass. Easily the best in town.",
  },
  {
    name: "Sienna Marchetti",
    role: "Porsche Macan",
    rating: 5,
    text: "I work 60+ hour weeks. The fact that they show up at my office and detail while I'm in meetings is unbeatable. Quality is top-tier.",
  },
  {
    name: "Devin Park",
    role: "Audi RS6 Avant",
    rating: 5,
    text: "Headlight restoration brought a 2017 back to life. Plus they wax the door jambs — that's attention to detail you don't see from chain shops.",
  },
  {
    name: "Alyssa Tran",
    role: "Lexus RX",
    rating: 5,
    text: "Booked a Full Detail before resale — got $1,500 more than blue book. Worth it 10x over. Highly, highly recommend Velocity.",
  },
];

export const GALLERY = [
  {
    title: "Black sedan — paint correction",
    before: "https://images.pexels.com/photos/3354648/pexels-photo-3354648.jpeg",
    after: "https://images.pexels.com/photos/17081564/pexels-photo-17081564.jpeg",
  },
];

export const FAQS = [
  {
    q: "How long does a detail take?",
    a: "Maintenance services run 1.5–3 hours; Premium Exterior or Deep Interior 3–5 hours; the Premium Reset Detail typically takes 5–7 hours. We'll confirm an accurate window with your quote.",
  },
  {
    q: "What's the difference between Maintenance and Premium services?",
    a: "Maintenance services keep an already well-cared-for vehicle looking its best. Premium / Deep services involve decontamination, restoration and rebuilding the finish — for vehicles that need more than a routine clean.",
  },
  {
    q: "Are your products safe for ceramic coatings & PPF?",
    a: "Absolutely. We use pH-neutral shampoos and coating-safe sealants. Let us know what's on the vehicle at booking.",
  },
  {
    q: "What if it rains?",
    a: "We monitor the forecast. If weather is unsafe, we'll reach out to reschedule at no charge — your appointment slot is held.",
  },
];

// Backwards-compatibility alias (other components may still import this)
export const PACKAGE_CATEGORIES = SERVICE_CATEGORIES;
